import { useState, useCallback, useMemo } from 'react';
import type { BookingStepV4, UseStepNavigationReturn } from '../types';

/**
 * Step order for V4 booking form.
 * Room selection is always first, followed by dates/guests, then guest details.
 * Package selection is integrated into the room selection step as "Enhance Your Stay".
 */
const STEP_ORDER: BookingStepV4[] = [
  'room-selection',
  'date-guest',
  'guest-details',
];

interface UseStepNavigationOptions {
  isRoomStepComplete: boolean;
  isDateGuestStepComplete: boolean;
  isGuestDetailsStepComplete: boolean;
}

/**
 * Hook for managing step navigation in V4 booking form.
 * Handles step progression, validation gates, and navigation logic.
 */
export function useStepNavigation(
  options: UseStepNavigationOptions,
): UseStepNavigationReturn {
  const { isRoomStepComplete, isDateGuestStepComplete, isGuestDetailsStepComplete } =
    options;

  // Always start at room selection
  const [currentStep, setCurrentStep] = useState<BookingStepV4>('room-selection');

  // Calculate completed steps based on validation
  const completedSteps = useMemo(() => {
    const completed: BookingStepV4[] = [];

    if (isRoomStepComplete) {
      completed.push('room-selection');
    }

    if (isDateGuestStepComplete) {
      completed.push('date-guest');
    }

    if (isGuestDetailsStepComplete) {
      completed.push('guest-details');
    }

    return completed;
  }, [isRoomStepComplete, isDateGuestStepComplete, isGuestDetailsStepComplete]);

  // Check if user can navigate to a specific step
  const canGoToStep = useCallback(
    (step: BookingStepV4): boolean => {
      const stepIndex = STEP_ORDER.indexOf(step);
      if (stepIndex === -1) return false;

      // Can always go to first step (room selection)
      if (stepIndex === 0) return true;

      // For other steps, check if previous steps are complete
      const previousSteps = STEP_ORDER.slice(0, stepIndex);
      return previousSteps.every((s) => {
        if (s === 'room-selection') return isRoomStepComplete;
        if (s === 'date-guest') return isDateGuestStepComplete;
        return true;
      });
    },
    [isRoomStepComplete, isDateGuestStepComplete],
  );

  const goToStep = useCallback(
    (step: BookingStepV4) => {
      if (canGoToStep(step)) {
        setCurrentStep(step);
      }
    },
    [canGoToStep],
  );

  const goToNextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      const nextStep = STEP_ORDER[currentIndex + 1];
      if (canGoToStep(nextStep)) {
        setCurrentStep(nextStep);
      }
    }
  }, [currentStep, canGoToStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const currentIndex = STEP_ORDER.indexOf(currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === STEP_ORDER.length - 1;

  return {
    currentStep,
    completedSteps,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canGoToStep,
    isFirstStep,
    isLastStep,
  };
}
